import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, useApi } from '@/api/client';
import { fmtDate } from '@/lib/format';
import type { AdminMcpServer, McpServerTool } from '@/types';

function errText(e: unknown): string {
  if (e instanceof ApiError) return e.detail ?? e.message;
  return e instanceof Error ? e.message : 'Something went wrong.';
}

export function McpServers() {
  const api = useApi();
  const servers = useQuery({ queryKey: ['admin-mcp-servers'], queryFn: api.adminListMcpServers });

  return (
    <>
      <div className="page-header">
        <div>
          <h1>MCP servers</h1>
          <div className="sub">
            Tool servers the gateway can call during completions. Callers get access per server
            on the Callers page (default deny); tool usage is reported under Usage.
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 22 }}>
        <div className="card-header"><h3>Registered servers</h3></div>
        {servers.error ? (
          <div className="card-body">
            <div className="error-banner" style={{ marginBottom: 0 }}>Could not load servers: {errText(servers.error)}</div>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Server</th>
                <th>Endpoint</th>
                <th>Enabled</th>
                <th>Auth</th>
                <th>Tools</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {servers.isLoading ? (
                <tr><td colSpan={6} className="placeholder">Loading servers...</td></tr>
              ) : (servers.data ?? []).length === 0 ? (
                <tr><td colSpan={6} className="placeholder">No MCP servers registered yet. Add the first one below.</td></tr>
              ) : (servers.data ?? []).map(s => <ServerRow key={s.id} server={s} />)}
            </tbody>
          </table>
        )}
      </div>

      <AddServerCard />
    </>
  );
}

function ServerRow({ server }: { server: AdminMcpServer }) {
  const api = useApi();
  const qc = useQueryClient();
  const [showTools, setShowTools] = useState(false);
  const [keyValue, setKeyValue] = useState('');

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-mcp-servers'] });

  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api.adminSetMcpServerEnabled(server.name, enabled),
    onSuccess: invalidate
  });

  const setKey = useMutation({
    mutationFn: () => api.adminSetMcpServerKey(server.name, keyValue),
    onSuccess: () => { setKeyValue(''); invalidate(); }
  });

  const error = (toggle.error ?? setKey.error) as Error | null;

  return (
    <>
      <tr>
        <td>
          <div className="row-title">{server.name}</div>
          <div className="row-sub">{server.description ?? '—'}</div>
        </td>
        <td style={{ maxWidth: 280, wordBreak: 'break-all', fontSize: '.82rem' }}>{server.baseUrl}</td>
        <td>
          <label className="switch">
            <input
              type="checkbox"
              checked={server.enabled}
              disabled={toggle.isPending}
              onChange={e => toggle.mutate(e.target.checked)}
              aria-label={`Enable ${server.name}`}
            />
            <span className="track"></span>
          </label>
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
          {server.requiresUserCredential ? (
            <>
              <div style={{ fontSize: '.78rem', color: 'var(--kt-grey-500)' }}>{server.authHeaderName ?? '—'}</div>
              <span className="pill pill-info" title="No shared key — each caller supplies their own credential per request, so the server runs as that caller.">
                per-caller key
              </span>
            </>
          ) : server.authHeaderName ? (
            <>
              <div style={{ fontSize: '.78rem', color: 'var(--kt-grey-500)' }}>{server.authHeaderName}</div>
              <span className={`pill ${server.hasKey ? 'pill-success' : 'pill-pending'}`}>
                {server.hasKey ? 'key stored' : 'key missing'}
              </span>
              <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
                <input
                  className="cell-input"
                  style={{ width: 130 }}
                  type="password"
                  placeholder={server.hasKey ? 'Rotate key' : 'Set key'}
                  value={keyValue}
                  onChange={e => setKeyValue(e.target.value)}
                  aria-label={`Auth key for ${server.name}`}
                />
                <button
                  className="btn-secondary btn-sm"
                  disabled={keyValue.trim().length < 8 || setKey.isPending}
                  onClick={() => setKey.mutate()}>
                  {setKey.isPending ? '...' : 'Save'}
                </button>
              </div>
            </>
          ) : (
            <span style={{ color: 'var(--kt-grey-400)', fontSize: '.82rem' }}>No auth header</span>
          )}
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
          <span style={{ fontSize: '.85rem', color: server.allowedTools ? 'var(--kt-orange)' : 'var(--kt-grey-500)' }}>
            {server.allowedTools ? `${server.allowedTools.length} allowed` : 'All tools'}
          </span>{' '}
          <button className="btn-ghost btn-sm" onClick={() => setShowTools(v => !v)}>
            {showTools ? 'Close' : 'Manage'}
          </button>
        </td>
        <td style={{ textAlign: 'right' }}>
          {error && <div style={{ color: 'var(--kt-red)', fontSize: '.78rem', whiteSpace: 'normal' }}>{errText(error)}</div>}
          <div style={{ fontSize: '.72rem', color: 'var(--kt-grey-400)' }}>
            {fmtDate(server.updatedAtUtc)}{server.updatedBy ? ` · ${server.updatedBy}` : ''}
          </div>
        </td>
      </tr>
      {showTools && (
        <tr>
          <td colSpan={6}><ToolManager server={server} onSaved={invalidate} /></td>
        </tr>
      )}
    </>
  );
}

/** Live tool listing with allowlist checkboxes — also doubles as the connectivity test. */
function ToolManager({ server, onSaved }: { server: AdminMcpServer; onSaved: () => void }) {
  const api = useApi();
  const tools = useQuery<McpServerTool[]>({
    queryKey: ['admin-mcp-tools', server.name],
    queryFn: () => api.adminMcpServerTools(server.name),
    retry: false
  });
  const [restricted, setRestricted] = useState(server.allowedTools !== null);
  const [selected, setSelected] = useState<Set<string>>(new Set(server.allowedTools ?? []));

  const save = useMutation({
    mutationFn: () =>
      api.adminUpsertMcpServer({
        name: server.name,
        baseUrl: server.baseUrl,
        enabled: server.enabled,
        description: server.description ?? undefined,
        authHeaderName: server.authHeaderName ?? undefined,
        requiresUserCredential: server.requiresUserCredential,
        allowedTools: restricted ? Array.from(selected) : []
      }),
    onSuccess: onSaved
  });

  if (tools.isLoading) return <div className="placeholder">Connecting to {server.name}...</div>;
  if (tools.error) {
    return <div className="error-banner" style={{ margin: 8 }}>Could not reach the server: {errText(tools.error)}</div>;
  }

  return (
    <div style={{ padding: '12px 4px' }}>
      <div style={{ fontSize: '.8rem', color: 'var(--kt-grey-500)', marginBottom: 8 }}>
        Connected — {tools.data?.length ?? 0} tools reported.
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, marginBottom: 10 }}>
        <input type="checkbox" checked={!restricted} onChange={e => setRestricted(!e.target.checked)} />
        All tools (no restriction)
      </label>
      {restricted && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 6, marginBottom: 10 }}>
          {(tools.data ?? []).map(t => (
            <label key={t.name} title={t.description ?? undefined} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: '.85rem' }}>
              <input
                type="checkbox"
                checked={selected.has(t.name)}
                onChange={() => setSelected(prev => {
                  const next = new Set(prev);
                  if (next.has(t.name)) next.delete(t.name); else next.add(t.name);
                  return next;
                })}
                style={{ marginTop: 3 }}
              />
              <span>
                <strong>{t.name}</strong>
                {t.description && <span style={{ display: 'block', color: 'var(--kt-grey-500)', fontSize: '.78rem' }}>{t.description}</span>}
              </span>
            </label>
          ))}
        </div>
      )}
      {save.error && <div className="error-banner">{errText(save.error)}</div>}
      <button className="btn-primary btn-sm" onClick={() => save.mutate()} disabled={save.isPending}>
        {save.isPending ? 'Saving...' : 'Save tool access'}
      </button>
    </div>
  );
}

function AddServerCard() {
  const api = useApi();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [description, setDescription] = useState('');
  const [authHeaderName, setAuthHeaderName] = useState('X-MCP-Key');
  const [requiresUserCredential, setRequiresUserCredential] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      api.adminUpsertMcpServer({
        name: name.trim(),
        baseUrl: baseUrl.trim(),
        enabled: true,
        description: description.trim() || undefined,
        authHeaderName: authHeaderName.trim() || undefined,
        requiresUserCredential
      }),
    onSuccess: () => {
      setName(''); setBaseUrl(''); setDescription(''); setRequiresUserCredential(false);
      qc.invalidateQueries({ queryKey: ['admin-mcp-servers'] });
    }
  });

  // A user-credential server has no shared key, so it must name the header the caller's credential rides in.
  const canSubmit = /^[a-z0-9][a-z0-9-]*$/.test(name.trim())
    && baseUrl.trim().startsWith('http')
    && (!requiresUserCredential || authHeaderName.trim().length > 0)
    && !create.isPending;

  return (
    <div className="card" style={{ maxWidth: 720 }}>
      <div className="card-header"><h3>Register server</h3></div>
      <div className="card-body">
        {create.error && <div className="error-banner">Could not register: {errText(create.error)}</div>}
        <div className="field-row">
          <div className="field">
            <label htmlFor="mcp-name">Name</label>
            <input id="mcp-name" type="text" placeholder="e.g. atlas-investigator" value={name} onChange={e => setName(e.target.value)} />
            <div className="hint">Lowercase, digits and hyphens. Callers reference this name.</div>
          </div>
          <div className="field">
            <label htmlFor="mcp-url">MCP endpoint URL</label>
            <input id="mcp-url" type="text" placeholder="https://host/mcp" value={baseUrl} onChange={e => setBaseUrl(e.target.value)} />
            <div className="hint">Streamable-HTTP endpoint reachable from the gateway.</div>
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="mcp-desc">Description</label>
            <input id="mcp-desc" type="text" placeholder="What do these tools do?" value={description} onChange={e => setDescription(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="mcp-auth">Auth header{requiresUserCredential ? '' : ' (blank = none)'}</label>
            <input id="mcp-auth" type="text" value={authHeaderName} onChange={e => setAuthHeaderName(e.target.value)} />
            <div className="hint">
              {requiresUserCredential
                ? 'Header the caller’s credential is sent in (e.g. Ocp-Apim-Subscription-Key).'
                : 'Set the key itself after registering (stored in Key Vault).'}
            </div>
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: '4px 0 16px', fontSize: '.88rem' }}>
          <input
            type="checkbox"
            checked={requiresUserCredential}
            onChange={e => setRequiresUserCredential(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>
            <strong>Each caller supplies their own credential</strong>
            <span style={{ display: 'block', color: 'var(--kt-grey-500)', fontSize: '.8rem' }}>
              No shared key is stored. Callers pass their own subscription key/token per request and the
              server runs as them, honouring their own permissions (e.g. kt-database-access). Requires an auth header.
            </span>
          </span>
        </label>
        <button className="btn-primary" onClick={() => create.mutate()} disabled={!canSubmit}>
          {create.isPending ? 'Registering...' : 'Register server'}
        </button>
      </div>
    </div>
  );
}
